import { X, ExternalLink } from "lucide-react";
import { SocialIcon } from "@/components/ui/LinkIcon";

const PLATFORM_WORDMARKS = {
  playboy: "/icons/playboy-wordmark.png",
  mym: "/icons/mym-wordmark.png",
};

const NSFW_ICONS = new Set(["onlyfans", "playboy", "mym", "mymfans", "snapchat"]);
const NSFW_URL_PATTERNS = [
  /onlyfans\.com/i,
  /playboy\.com/i,
  /mym\.fans/i,
  /mymfans/i,
  /fansly\.com/i,
];

const isNsfwLink = (link) => {
  if (!link) return false;
  if (link.nsfw === true) return true;
  if (link.icon && NSFW_ICONS.has(String(link.icon).toLowerCase())) return true;
  const url = link.url || "";
  return NSFW_URL_PATTERNS.some((re) => re.test(url));
};

const LinkPopupModal = ({ link, isVideo, ensureProtocol, onClose }) => {
  if (!link) return null;
  const finalUrl = ensureProtocol(link.url);
  const wordmark = PLATFORM_WORDMARKS[link.icon];
  const nsfw = isNsfwLink(link);

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="link-popup-modal"
    >
      <div
        className="relative w-full max-w-md bg-[#0a0a0a] border border-white/10 rounded-3xl overflow-hidden shadow-2xl animate-in slide-in-from-bottom-4 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Cerrar"
          data-testid="link-popup-close"
          className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-black/50 border border-white/15 flex items-center justify-center text-white/80 hover:text-white hover:bg-black/70 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Media */}
        <div
          className={`relative w-full overflow-hidden ${
            link.imageDisplay === "vertical"
              ? "aspect-[3/4]"
              : link.imageDisplay === "horizontal"
              ? "aspect-video"
              : "aspect-square"
          }`}
        >
          {link.video || isVideo(link.image) ? (
            <video
              src={link.video || link.image}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
            />
          ) : link.image ? (
            <img src={link.image} alt={link.title} className="w-full h-full object-cover" />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ backgroundColor: link.bgColor || "#1a1a1a" }}
            >
              <SocialIcon platform={link.icon || "globe"} className="w-20 h-20 text-white" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent pointer-events-none" />

          {link.badge && link.badge.trim() && (
            <div className="absolute top-3 left-3 z-10 pointer-events-none">
              <div
                className="relative overflow-hidden px-3 py-1.5 rounded-full backdrop-blur-md shadow-md ring-1 ring-white/20 animate-badge-fade"
                style={{
                  backgroundColor: link.bgColor || "#1a1a1a",
                  color: link.textColor || "#ffffff",
                }}
              >
                <span className="relative z-10 font-semibold text-[11px] tracking-[0.08em] uppercase whitespace-nowrap">
                  {link.badge}
                </span>
                <span className="badge-shimmer" aria-hidden />
              </div>
            </div>
          )}

          <div className="absolute bottom-4 left-4 right-4">
            {wordmark ? (
              <img
                src={wordmark}
                alt={link.title}
                className="h-10 w-auto max-w-[70%] object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]"
              />
            ) : (
              <h3 className="text-2xl font-bold text-white drop-shadow-lg">{link.title}</h3>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="p-5 space-y-3">
          <a
            href={finalUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-nsfw={nsfw ? "true" : "false"}
            data-testid="link-popup-visit-btn"
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-white text-black font-bold rounded-xl hover:bg-white/90 transition-colors text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            Visitar enlace
          </a>
          <button
            onClick={onClose}
            className="w-full py-2.5 text-white/50 hover:text-white text-xs font-medium transition-colors"
          >
            Tal vez más tarde
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkPopupModal;
